import { supabase } from '../lib/supabase';
import { initiateCampayElearningEdgePayment, checkCampayElearningEdgePaymentStatus } from './campayElearningEdgeService';
import { processPayment } from './paymentService';

interface ElearningPaymentOptions {
  paymentMethod: string;
  phoneNumber?: string;
}

interface ElearningOrder {
  id: string;
  order_number: string;
  total_amount: number;
  customer_name: string;
  customer_email: string;
}

/**
 * Initialise un paiement pour une commande e-learning
 */
export const initiateElearningPayment = async (order: ElearningOrder, options: ElearningPaymentOptions) => {
  try {
    // Vérifier si la méthode de paiement est MTN ou Orange (Campay)
    if (['mtn', 'orange'].includes(options.paymentMethod)) {
      // Utiliser le service dédié pour les paiements e-learning
      const response = await initiateCampayElearningEdgePayment({
        amount: order.total_amount,
        phoneNumber: options.phoneNumber || '',
        operator: options.paymentMethod,
        order_id: order.id,
        order_number: order.order_number,
        customerName: order.customer_name,
        customerEmail: order.customer_email,
        description: `Commande #${order.order_number} - Formation E-learning`
      });
      
      // Mettre à jour la commande avec les informations de paiement
      await supabase
        .from('elearn_orders')
        .update({ 
          payment_reference: response.payment_reference,
          payment_method: options.paymentMethod,
          payment_url: response.payment_url,
          status: 'pending_payment' // Mettre à jour le statut de la commande
        })
        .eq('id', order.id);
      
      return response;
    } else {
      // Pour les autres méthodes de paiement, utiliser le système existant
      const paymentData = {
        amount: order.total_amount,
        votes_count: 1, // Valeur requise par l'API existante
        paymentMethod: options.paymentMethod,
        phoneNumber: options.phoneNumber,
        contestantId: '00000000-0000-0000-0000-000000000000', // ID générique pour les transactions hors votes
        customerName: order.customer_name,
        customerEmail: order.customer_email,
        description: `Commande #${order.order_number} - Formation E-learning`
      };

      const response = await processPayment(paymentData);
      
      // Mettre à jour la commande avec les informations de paiement
      await supabase
        .from('elearn_orders')
        .update({ 
          payment_reference: response.payment_reference,
          payment_method: options.paymentMethod,
          payment_url: response.payment_url,
          status: 'pending_payment' // Mettre à jour le statut de la commande
        })
        .eq('id', order.id);
      
      return response;
    }
  } catch (error) {
    console.error('Erreur lors de l\'initialisation du paiement e-learning:', error);
    throw new Error(`Erreur lors de l'initialisation du paiement: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
  }
};

/**
 * Vérifie le statut d'un paiement pour une commande e-learning
 */
export const checkElearningPaymentStatus = async (orderId: string) => {
  try {
    // Récupérer les informations de la commande
    const { data: order, error } = await supabase
      .from('elearn_orders')
      .select('payment_reference, payment_method')
      .eq('id', orderId)
      .single();
    
    if (error || !order || !order.payment_reference || !order.payment_method) {
      throw new Error('Commande introuvable ou informations de paiement manquantes');
    }
    
    // Utiliser le service dédié pour vérifier le statut du paiement e-learning
    const paymentStatus = await checkCampayElearningEdgePaymentStatus(order.payment_reference, orderId);
    return paymentStatus;
  } catch (error) {
    console.error('Erreur lors de la vérification du statut de paiement e-learning:', error);
    throw new Error(`Erreur lors de la vérification du paiement: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
  }
};

/**
 * Marque une commande e-learning comme payée et crée les inscriptions associées
 */
export const markElearningOrderAsPaid = async (orderId: string) => {
  try {
    // Mettre à jour le statut de la commande
    const { data: order, error } = await supabase
      .from('elearn_orders')
      .update({ status: 'completed' })
      .eq('id', orderId)
      .select('*')
      .single();
    
    if (error) throw error;
    
    // Récupérer les articles de la commande
    const { data: orderItems } = await supabase
      .from('elearn_order_items')
      .select('*')
      .eq('order_id', orderId);
    
    if (!orderItems || orderItems.length === 0) {
      return { success: true, order };
    }
    
    // Créer les inscriptions pour chaque cours
    // Cette partie est déjà gérée par le webhook ou la fonction de vérification
    
    return { success: true, order };
  } catch (error) {
    console.error('Erreur lors de la mise à jour du statut de la commande e-learning:', error);
    throw new Error(`Erreur lors de la mise à jour: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
  }
};

/**
 * Crée les inscriptions aux cours pour une commande payée
 * Cette fonction est appelée après confirmation du paiement
 */
export const createEnrollmentsForPaidOrder = async (orderId: string) => {
  try {
    // Récupérer les informations de la commande
    const { data: order, error: orderError } = await supabase
      .from('elearn_orders')
      .select('*')
      .eq('id', orderId)
      .single();
    
    if (orderError || !order) {
      throw new Error('Commande introuvable');
    }
    
    // Récupérer les articles de la commande
    const { data: orderItems, error: itemsError } = await supabase
      .from('elearn_order_items')
      .select('*')
      .eq('order_id', orderId);
    
    if (itemsError || !orderItems || orderItems.length === 0) {
      throw new Error('Articles de commande introuvables');
    }
    
    // Récupérer le profil client associé à la commande
    const { data: customerProfile, error: profileError } = await supabase
      .from('elearn_customer_profiles')
      .select('*')
      .eq('id', order.customer_profile_id)
      .single();
    
    if (profileError || !customerProfile) {
      throw new Error('Profil client introuvable');
    }
    
    // Vérifier si l'email du client correspond à un utilisateur existant
    const { data: userProfile } = await supabase
      .auth
      .admin
      .listUsers({
        filters: {
          email: customerProfile.email
        }
      });
    
    const userId = userProfile?.users && userProfile.users.length > 0 
      ? userProfile.users[0].id 
      : customerProfile.id; // Utiliser l'ID du profil client comme fallback
    
    // Créer les inscriptions pour chaque cours
    const enrollments = [];
    
    for (const item of orderItems) {
      // Vérifier si une inscription existe déjà
      const { data: existingEnrollment } = await supabase
        .from('elearn_enrollments')
        .select('id')
        .eq('tenant_id', order.tenant_id)
        .eq('student_id', userId)
        .eq('course_id', item.course_id)
        .single();
      
      if (!existingEnrollment) {
        // Créer une nouvelle inscription
        const { data: enrollment, error: enrollmentError } = await supabase
          .from('elearn_enrollments')
          .insert({
            tenant_id: order.tenant_id,
            domain: order.domain,
            student_id: userId,
            course_id: item.course_id,
            order_id: orderId,
            status: 'active',
            progress_percentage: 0
          })
          .select()
          .single();
        
        if (enrollmentError) {
          console.error('Erreur lors de la création de l\'inscription:', enrollmentError);
          continue;
        }
        
        enrollments.push(enrollment);
      }
    }
    
    return { success: true, enrollments };
  } catch (error) {
    console.error('Erreur lors de la création des inscriptions:', error);
    throw new Error(`Erreur lors de la création des inscriptions: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
  }
};
